import { useHandler } from "@redotech/react-util/hook";
import { PageContext } from "@redotech/redo-model/agents/page-context";
import { RedoInternalProduct } from "@redotech/redo-model/redo-internal-products";
import { ToolComponentProps } from "@redotech/redo-web/ai/chat/types/tool-component-props";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { nativeRandom, shuffle } from "@redotech/util/random";
import {
  type ComponentType,
  createContext,
  type ReactNode,
  type SVGProps,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { getActiveProductNames } from "./active-products";
import { resolveEffectivePageContext } from "./ask-redo/page-context-resolve";

export interface AgentToolComponents {
  [key: string]: ((args: ToolComponentProps) => ReactNode) | undefined;
}

export interface AgentToolResultHandlers {
  [key: string]: ((result: unknown) => void) | undefined;
}

/**
 * Browser-side executors for agent tools that have no server-side execute
 * (client-executed tools). Keyed by tool name; the executor receives the
 * tool-call input and returns the tool output (thrown errors become tool
 * error results). Pages register these on mount — e.g. the Coverage
 * Builder page executes screenshot tools against its live preview iframe —
 * and reset to undefined on unmount.
 */
export interface AgentClientToolExecutors {
  [key: string]: ((input: unknown) => Promise<unknown>) | undefined;
}

/**
 * A page-contributed reference the merchant wants the Ask Redo agent to know
 * about. Pages call `pin()` with one of these; the prompt-input renders a
 * removable chip per pin above the textarea, and each pin's `pageData` gets
 * merged into the outgoing `pageContext.pageData` snapshot so the agent
 * receives the structured payload alongside the page description.
 *
 * Designed to be generic: a new "pin an order", "pin a return", etc. needs
 * only a `pin()` call from its page — no new context, no chip changes.
 */
export interface PinnedReference {
  /** Stable id used for dedup, render keys, and `unpin(id)`. */
  id: string;
  /**
   * Domain tag — `"conciergeMessage"`, `"order"`, etc. Surfaced to tooling
   * and useful for analytics; the chip doesn't branch on it.
   */
  type: string;
  /**
   * Human-readable type name for the chip header — e.g. "Concierge
   * reply", "Order", "Return". Distinct from `type` (which is a stable
   * machine tag); `kind` is what merchants read. Optional: when omitted
   * the chip falls back to label-only rendering.
   */
  kind?: string;
  /** Short text the chip displays. Truncated by CSS. */
  label: string;
  /** Optional 16px icon rendered before the label. */
  Icon?: ComponentType<SVGProps<SVGSVGElement>>;
  /**
   * Free-form payload merged into `pageContext.pageData` under a key the
   * caller controls. Multiple pins with overlapping keys: last pin wins —
   * pages should namespace (e.g. `{ selectedConversationMessage: {...} }`).
   */
  pageData?: Record<string, unknown>;
}

export interface GlobalAgentContextType {
  /**
   * Effective page context the chat transport sends to the agent. Equal to
   * the page-set context plus a merged `pageData` that folds in every
   * current `PinnedReference`'s `pageData`. Pages should not read this to
   * derive their own UI — read `pinnedReferences` instead.
   */
  pageContext?: PageContext;
  setPageContext: (context: PageContext | undefined) => void;
  /**
   * The page-set context exactly as the current page provided it —
   * before pin-data merging and before dismissal stripping. Read by the
   * page-context chip so its hover card shows what the page contributed
   * (and only that). Transports must keep sending `pageContext`.
   */
  basePageContext?: PageContext;
  /**
   * True when the merchant clicked × on the page-context chip. While
   * dismissed, the effective `pageContext` carries no `pageDescription`
   * and no page-set `pageData` — the agent stops seeing what page the
   * merchant is on. Skill hints (`preferredSkill`/`requiredSkill`),
   * `suggestedQuestions`, and explicitly pinned references still flow:
   * dismissal means "don't read over my shoulder", not "forget how to
   * route" or "drop what I deliberately attached".
   */
  pageContextDismissed: boolean;
  /**
   * Dismiss the current page's context. Resets automatically when the
   * page-set context clears — pages call `setPageContext(undefined)` on
   * unmount, so navigating anywhere re-attaches the next page's context.
   */
  dismissPageContext: () => void;
  defaultSuggestedQuestions: string[];
  onPageDataChanged?: () => void;
  setOnPageDataChanged: (callback: (() => void) | undefined) => void;
  /**
   * Tool components contributed by the currently mounted page. Pages call
   * setToolComponents on mount and reset to undefined on unmount so the
   * global Ask Redo chat can render page-specific tools.
   */
  toolComponents?: AgentToolComponents;
  setToolComponents: (components: AgentToolComponents | undefined) => void;
  toolResultHandlers?: AgentToolResultHandlers;
  setToolResultHandlers: (
    handlers: AgentToolResultHandlers | undefined,
  ) => void;
  clientToolExecutors?: AgentClientToolExecutors;
  setClientToolExecutors: (
    executors: AgentClientToolExecutors | undefined,
  ) => void;
  /**
   * Ordered list of references the merchant has pinned to the chat — e.g.
   * a Concierge message they want the agent to talk about, an order, a
   * return, etc. The chip row above the prompt input renders one chip per
   * pin; each pin's `pageData` is folded into `pageContext.pageData`.
   */
  pinnedReferences: PinnedReference[];
  /** Add or replace a pin (matched on `id`). */
  pin: (ref: PinnedReference) => void;
  /** Remove a pin by id. No-op if no pin matches. */
  unpin: (id: string) => void;
  /** Drop every pin. Called by the prompt input on successful send. */
  clearPins: () => void;
  /**
   * In-memory map of chat-message id → the pins that were attached when
   * that message was sent. Populated by `attachPinsToMessage` (called
   * from the chat renderer when a new user message lands). Used by the
   * conversation view to render pin chips above each historical user
   * message — the visible record of "this message was sent with this
   * Concierge reply attached". In-memory only: cleared on page reload
   * and not synced to the backend, so re-hydrating a thread loses the
   * chips. Acceptable trade-off for the 90% case (merchant pins, asks,
   * moves on) without paying the schema-migration cost.
   */
  messagePins: ReadonlyMap<string, readonly PinnedReference[]>;
  /**
   * Snapshot the given pins as "pending" so the very next user message
   * to land in the chat can claim them. The prompt input calls this
   * synchronously inside `handleSend`, passing the pins it sees in its
   * closure; the conversation view drains the snapshot the first time
   * it sees a new user message id. Passing the array explicitly (vs
   * reading state inside the provider) makes the snapshot truly
   * synchronous — no setState-callback timing to worry about. Pass an
   * empty array to no-op.
   */
  snapshotPinsForNextMessage: (pins: readonly PinnedReference[]) => void;
  /**
   * Move the pending snapshot (if any) onto the given message id. Once
   * drained, the snapshot is consumed so two user messages in a row
   * don't both claim the same pins.
   */
  attachPinsToMessage: (messageId: string) => void;
}

export const GlobalAgentContext = createContext<GlobalAgentContextType>({
  pageContext: undefined,
  setPageContext: () => {},
  basePageContext: undefined,
  pageContextDismissed: false,
  dismissPageContext: () => {},
  defaultSuggestedQuestions: [],
  onPageDataChanged: undefined,
  setOnPageDataChanged: () => {},
  toolComponents: undefined,
  setToolComponents: () => {},
  toolResultHandlers: undefined,
  setToolResultHandlers: () => {},
  clientToolExecutors: undefined,
  setClientToolExecutors: () => {},
  pinnedReferences: [],
  pin: () => {},
  unpin: () => {},
  clearPins: () => {},
  messagePins: new Map(),
  snapshotPinsForNextMessage: (_pins) => {},
  attachPinsToMessage: () => {},
});

export interface GlobalAgentProviderProps {
  children: React.ReactNode;
}

export function GlobalAgentProvider({ children }: GlobalAgentProviderProps) {
  const [basePageContext, setBasePageContext] = useState<
    PageContext | undefined
  >(undefined);
  const [pageContextDismissed, setPageContextDismissed] = useState(false);

  // Wrapped setter so dismissal resets exactly when the page-set context
  // clears. Pages call `setPageContext(undefined)` in their unmount
  // cleanup, so this fires on every navigation away from a context-aware
  // page — giving × its "cleared until you navigate" lifetime without
  // the provider needing router access (it mounts outside RouterProvider).
  // In-page updates (filter/sort changes re-calling setPageContext with a
  // fresh object) never pass through undefined, so they keep the
  // dismissal.
  const setPageContext = useCallback((context: PageContext | undefined) => {
    setBasePageContext(context);
    if (context === undefined) {
      setPageContextDismissed(false);
    }
  }, []);
  const dismissPageContext = useCallback(
    () => setPageContextDismissed(true),
    [],
  );
  const [pinnedReferences, setPinnedReferences] = useState<PinnedReference[]>(
    [],
  );
  const [onPageDataChanged, setOnPageDataChanged] = useState<
    (() => void) | undefined
  >(undefined);
  const [toolComponents, setToolComponents] = useState<
    AgentToolComponents | undefined
  >(undefined);
  const [toolResultHandlers, setToolResultHandlers] = useState<
    AgentToolResultHandlers | undefined
  >(undefined);
  const [clientToolExecutors, setClientToolExecutors] = useState<
    AgentClientToolExecutors | undefined
  >(undefined);

  const defaultSuggestedQuestions = useDefaultSuggestedQuestions();

  const stableSetOnPageDataChanged = useHandler(
    (cb: (() => void) | undefined) => setOnPageDataChanged(() => cb),
  );

  // Pins API. Identity-stable callbacks so consumers can list them in
  // useEffect deps without forcing re-runs each render.
  const pin = useCallback((ref: PinnedReference) => {
    setPinnedReferences((current) => {
      const filtered = current.filter((existing) => existing.id !== ref.id);
      return [...filtered, ref];
    });
  }, []);
  const unpin = useCallback((id: string) => {
    setPinnedReferences((current) =>
      current.filter((existing) => existing.id !== id),
    );
  }, []);
  const clearPins = useCallback(() => {
    setPinnedReferences((current) => (current.length === 0 ? current : []));
  }, []);

  // Map of chat-message id → pins attached to that message. State (not
  // ref) because the chat renderer subscribes to it to draw chips. The
  // pending snapshot, in contrast, is a ref — it changes don't need to
  // trigger a re-render; only the eventual drain into `messagePins`
  // does.
  const [messagePins, setMessagePins] = useState<
    ReadonlyMap<string, readonly PinnedReference[]>
  >(() => new Map());
  const pendingPinSnapshotRef = useRef<readonly PinnedReference[] | null>(null);

  const snapshotPinsForNextMessage = useCallback(
    (pins: readonly PinnedReference[]) => {
      pendingPinSnapshotRef.current = pins.length === 0 ? null : pins.slice();
    },
    [],
  );

  const attachPinsToMessage = useCallback((messageId: string) => {
    const snapshot = pendingPinSnapshotRef.current;
    if (!snapshot || snapshot.length === 0) {
      return;
    }
    pendingPinSnapshotRef.current = null;
    setMessagePins((current) => {
      // Map id collision shouldn't happen — message ids are unique —
      // but if it does, last write wins, which is the right behavior
      // for the regenerate-message edge case.
      const next = new Map(current);
      next.set(messageId, snapshot);
      return next;
    });
  }, []);

  // Effective page context the transport sends to the agent: dismissal
  // stripping + pin-pageData merging, in the pure helper. Pages don't
  // have to wire pins into pageContext themselves — `pin()` is the only
  // API.
  const pageContext = useMemo<PageContext | undefined>(
    () =>
      resolveEffectivePageContext(
        basePageContext,
        pinnedReferences,
        pageContextDismissed,
      ),
    [basePageContext, pageContextDismissed, pinnedReferences],
  );

  const contextValue = useMemo(
    () => ({
      pageContext,
      setPageContext,
      basePageContext,
      pageContextDismissed,
      dismissPageContext,
      defaultSuggestedQuestions,
      onPageDataChanged,
      setOnPageDataChanged: stableSetOnPageDataChanged,
      toolComponents,
      setToolComponents,
      toolResultHandlers,
      setToolResultHandlers,
      clientToolExecutors,
      setClientToolExecutors,
      pinnedReferences,
      pin,
      unpin,
      clearPins,
      messagePins,
      snapshotPinsForNextMessage,
      attachPinsToMessage,
    }),
    [
      pageContext,
      setPageContext,
      basePageContext,
      pageContextDismissed,
      dismissPageContext,
      defaultSuggestedQuestions,
      onPageDataChanged,
      stableSetOnPageDataChanged,
      toolComponents,
      toolResultHandlers,
      clientToolExecutors,
      pinnedReferences,
      pin,
      unpin,
      clearPins,
      messagePins,
      snapshotPinsForNextMessage,
      attachPinsToMessage,
    ],
  );

  return (
    <GlobalAgentContext.Provider value={contextValue}>
      {children}
    </GlobalAgentContext.Provider>
  );
}

/**
 * Resolves the suggested-questions list a chat surface should show:
 * page-specific questions when the active page set any, otherwise
 * the team-level defaults.
 *
 * Empty-array semantics are intentional. Pages typically compute
 * `pageContext.suggestedQuestions` dynamically by conditional spreads
 * over page state (see e.g. `return-details.tsx` building the list
 * from a return's status flags) — a future page that filters down to
 * `[]` should degrade to the team defaults, NOT silently show
 * nothing. If we ever need explicit "no suggestions" we'll add a
 * separate field on `PageContext`.
 *
 * Single source of truth: legacy `HelpChatGlobal`, the new Ask Redo
 * page, and the Ask Redo slide-out all read from here.
 */
export function useResolvedSuggestedQuestions(): string[] {
  const { pageContext, defaultSuggestedQuestions } =
    useContext(GlobalAgentContext);
  return useMemo(() => {
    const fromPage = pageContext?.suggestedQuestions ?? [];
    return fromPage.length > 0 ? fromPage : defaultSuggestedQuestions;
  }, [pageContext?.suggestedQuestions, defaultSuggestedQuestions]);
}

function useDefaultSuggestedQuestions(): string[] {
  const team = useContext(TeamContext);

  return useMemo(() => {
    const activeProducts = getActiveProductNames(team?.redoInternalProducts);

    const suggestedQuestions: string[] = [];

    if (activeProducts.includes(RedoInternalProduct.RETURNS)) {
      suggestedQuestions.push(
        "What were my most returned products over the last 90 days?",
        "What were the most common return reasons last month?",
      );
    }
    if (activeProducts.includes(RedoInternalProduct.PACKAGE_PROTECTION)) {
      suggestedQuestions.push(
        "What percentage of my orders are eligible for package protection?",
        "How many orders had package protection last week?",
      );
    }

    return shuffle(suggestedQuestions, nativeRandom).slice(0, 3);
  }, [team?.redoInternalProducts]);
}
